import { Dispatch, SetStateAction, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import PaymentSummary from '../../PaymentSummary/PaymentSummary';
import { Medlemskapstype } from '../../api/medlemskapAPI';
import { ApiResource } from '../../api/ApiResource';
import FamilyInformationForm from './FamilyInformationForm';
import { useErrorPageRedirectIfFailed } from '../../hooks';
import { MedlemskapTypeId } from '../../MedlemskapType/MedlemskapTypeId';
import loader from '../../loading-spinner.module.scss';
import { MemberInput } from '../../types/MemberInput';
import { PersonalInformation } from '../../types/PersonalInformation';
import BackLink from '../../BackLink';
import * as constants from '../../constants';
import globalStyles from '../../App.module.scss';
import { PostalCodeResponse } from '../../api/postalCodeAPI';

interface FamiliemedlemPersonalInformationPageProps {
    postalCodesListResource: ApiResource<PostalCodeResponse>;
    medlemskapstypeListResource: ApiResource<Medlemskapstype[]>;
    memberInput: MemberInput;
    setHovedmedlemInput: Dispatch<SetStateAction<PersonalInformation>>;
    setPartnerInput: Dispatch<SetStateAction<PersonalInformation | undefined>>;
    setBarnInput: Dispatch<SetStateAction<PersonalInformation[] | undefined>>;
    setAmountToPay: Dispatch<SetStateAction<number>>;
    discountCode: string;
    setDiscountCode: Dispatch<SetStateAction<string>>;
    discount: number;
    setDiscount: Dispatch<SetStateAction<number>>;
}

export default function FamiliemedlemPersonalInformationPage({
    postalCodesListResource,
    medlemskapstypeListResource,
    memberInput,
    setHovedmedlemInput,
    setPartnerInput,
    setBarnInput,
    setAmountToPay,
    discountCode,
    setDiscountCode,
    discount,
    setDiscount,
}: FamiliemedlemPersonalInformationPageProps) {
    const navigate = useNavigate();
    useErrorPageRedirectIfFailed(medlemskapstypeListResource);

    useEffect(() => {
        window.scrollTo(0, 0);
        setDiscountCode('');
        setDiscount(0);
    }, [setDiscount, setDiscountCode]);

    if (medlemskapstypeListResource.status !== 'ok') {
        return <div className={loader.loader} />;
    }

    const medlemskapstypeList = medlemskapstypeListResource.data;
    const familiemedlemskapstype = medlemskapstypeList.find(
        (medlemskapstype) => medlemskapstype.id === MedlemskapTypeId.Familie
    );

    const onSubmit = (): void => {
        setHovedmedlemInput({
            ...memberInput.hovedmedlem,
            phoneNumber: memberInput.hovedmedlem.phoneNumber.replaceAll(' ', '').replace(/^\+47|^0047/, ''),
        });
        if (memberInput.partner) {
            setPartnerInput({
                ...memberInput.partner,
                phoneNumber: memberInput.partner.phoneNumber.replaceAll(' ', '').replace(/^\+47|^0047/, ''),
            });
        }
        if (memberInput.barn) {
            let children = [...memberInput.barn];
            children.forEach((child) => {
                child.phoneNumber.replaceAll(' ', '').replace(/^\+47|^0047/, '');
            });
            setBarnInput(children);
        }

        if (familiemedlemskapstype) {
            setAmountToPay(familiemedlemskapstype.price - discount);
            navigate(`/betaling/${familiemedlemskapstype.id}`, { replace: true });
        }
    };

    return (
        <div>
            <BackLink destination={constants.LINK_TO_START_PAGE} />

            {familiemedlemskapstype && (
                <div className={globalStyles.summary}>
                    <PaymentSummary
                        medlemskapstype={familiemedlemskapstype}
                        discountCode={discountCode}
                        discount={discount}
                    />
                </div>
            )}

            <FamilyInformationForm
                postalCodesListResource={postalCodesListResource}
                activeMedlemskap={familiemedlemskapstype}
                memberInput={memberInput}
                setHovedmedlemInput={setHovedmedlemInput}
                setPartnerInput={setPartnerInput}
                setBarnInput={setBarnInput}
                discountCode={discountCode}
                setDiscountCode={setDiscountCode}
                discount={discount}
                setDiscount={setDiscount}
                onSubmit={onSubmit}
            />
        </div>
    );
}
